import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import {
    ShoppingCart, Trash2, ChevronRight, Truck, Package, ArrowLeft,
    Plus, Minus, Wallet, CreditCard, CheckCircle, MapPin, Phone, User,
    ShoppingBag, AlertTriangle, Gift
} from 'lucide-react';
import { useAlert } from '../context/AlertContext';
import { useConfirm } from '../context/ConfirmContext';
import { useNavigate } from 'react-router-dom';
import { State, City } from 'country-state-city';
import API_BASE_URL from '../config/api';

const Checkout = () => {
    const [cartItemsByPackage, setCartItemsByPackage] = useState([]);
    const [loading, setLoading] = useState(true);
    const [placingOrder, setPlacingOrder] = useState(false);
    const [shipping, setShipping] = useState({
        name: '', address: '', city: '', state: '', zip: '', mobile: '',
        fulfillmentSource: 'admin', franchiseId: ''
    });
    const [franchises, setFranchises] = useState([]);
    const [step, setStep] = useState(1); // 1: Review, 2: Shipping, 3: Confirm
    const [states] = useState(State.getStatesOfCountry('IN'));
    const [cities, setCities] = useState([]);
    const [searchingPincode, setSearchingPincode] = useState(false);
    const { showAlert } = useAlert();
    const { showConfirm } = useConfirm();
    const navigate = useNavigate();

    useEffect(() => {
        fetchAllData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchAllData = async () => {
        try {
            const token = localStorage.getItem('authToken');

            // Fetch all cart items
            const cartResponse = await fetch(`${API_BASE_URL}/shop/cart.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const cartData = await cartResponse.json();

            // Fetch all packages
            const packagesResponse = await fetch(`${API_BASE_URL}/shop/products.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const packagesData = await packagesResponse.json();

            // Fetch available franchises
            const franchiseResponse = await fetch(`${API_BASE_URL}/shop/list_franchises.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const franchiseData = await franchiseResponse.json();
            if (franchiseData.success) {
                setFranchises(franchiseData.data);
            }

            if (cartData.success) {
                // Group cart items by package_id (type) instead of individual user_package_id
                const grouped = {};
                (cartData.data || []).forEach(item => {
                    // Find which package pool this item belongs to
                    const pkgPool = (packagesData.package_tabs || []).find(p => p.all_package_ids.includes(item.user_package_id));
                    const poolKey = pkgPool ? pkgPool.package_id : item.user_package_id;

                    if (!grouped[poolKey]) {
                        grouped[poolKey] = [];
                    }
                    grouped[poolKey].push(item);
                });

                // Convert to array with pooled package info
                const groupedArray = Object.keys(grouped).map(poolKey => {
                    const pkgPool = (packagesData.package_tabs || []).find(p => p.package_id.toString() === poolKey);
                    return {
                        user_package_id: pkgPool ? pkgPool.user_package_id : poolKey, // Use representative ID for API call
                        package_name: pkgPool ? pkgPool.package_name : 'Unknown Package',
                        remaining_balance: pkgPool ? pkgPool.remaining_balance : 0,
                        instance_count: pkgPool ? pkgPool.instance_count : 1,
                        original_price: pkgPool ? pkgPool.original_price : 0,
                        items: grouped[poolKey]
                    };
                });

                setCartItemsByPackage(groupedArray);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const removeItem = async (id) => {
        const confirmed = await showConfirm('Remove Item', 'Are you sure you want to remove this item?', 'warning');
        if (!confirmed) return;
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/shop/cart.php?id=${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                showAlert('Item removed', 'success');
                fetchAllData();
            }
        } catch (error) {
            showAlert('Failed to remove item', 'error');
        }
    };

    const updateQuantity = async (id, currentQty, change) => {
        const newQty = currentQty + change;
        if (newQty <= 0) {
            removeItem(id);
            return;
        }
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/shop/cart.php`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ id, quantity: newQty })
            });
            const data = await response.json();
            if (data.success) {
                fetchAllData();
            } else {
                showAlert(data.message, 'error');
            }
        } catch (error) {
            showAlert('Failed to update quantity', 'error');
        }
    };

    const handlePincodeLookup = async (pincode) => {
        if (pincode.length === 6) {
            setSearchingPincode(true);
            try {
                const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
                const data = await response.json();
                if (data[0].Status === "Success") {
                    const postOffice = data[0].PostOffice[0];
                    const stateName = postOffice.State;
                    const cityName = postOffice.District;

                    // Find state code
                    const stateObj = states.find(s => s.name.toLowerCase() === stateName.toLowerCase());
                    if (stateObj) {
                        const stateCities = City.getCitiesOfState('IN', stateObj.isoCode);
                        setCities(stateCities);
                        setShipping(prev => ({
                            ...prev,
                            state: stateName,
                            city: cityName,
                            zip: pincode
                        }));
                    }
                }
            } catch (error) {
                console.error("Pincode lookup failed", error);
            } finally {
                setSearchingPincode(false);
            }
        }
    };

    const handleStateChange = (stateName) => {
        const stateObj = states.find(s => s.name === stateName);
        if (stateObj) {
            const stateCities = City.getCitiesOfState('IN', stateObj.isoCode);
            setCities(stateCities);
            setShipping(prev => ({ ...prev, state: stateName, city: '' }));
        }
    };

    const handlePlaceAllOrders = async () => {
        // Validate shipping
        if (!shipping.name || !shipping.address || !shipping.city || !shipping.state || !shipping.zip || !shipping.mobile) {
            showAlert('Please fill in all shipping details', 'error');
            setStep(2);
            return;
        }

        if (shipping.fulfillmentSource === 'franchise' && !shipping.franchiseId) {
            showAlert('Please select a franchise', 'error');
            setStep(2);
            return;
        }

        // Check if all packages meet minimum item requirements (4 items normally, 1 for Starter 3700)
        const insufficientQuantityPackages = cartItemsByPackage.filter(pkg => {
            const itemsCount = pkg.items.reduce((a, i) => a + parseInt(i.quantity), 0);
            const minRequired = pkg.original_price === 3700 ? 1 : 4;
            return itemsCount < minRequired;
        });

        if (insufficientQuantityPackages.length > 0) {
            showAlert('Some packages do not meet the minimum item requirement (4 items, or 1 item for Starter package).', 'error');
            return;
        }

        // Check if all packages have sufficient balance
        const insufficientPackages = cartItemsByPackage.filter(pkg => {
            const pkgTotal = getPackageTotal(pkg.items);
            return parseFloat(pkg.remaining_balance) < pkgTotal;
        });

        if (insufficientPackages.length > 0) {
            showAlert(`${insufficientPackages.length} package(s) have insufficient balance. Please remove items or adjust quantities.`, 'error');
            return;
        }

        setPlacingOrder(true);
        const token = localStorage.getItem('authToken');
        let successCount = 0;
        let failCount = 0;

        // Place orders for each package
        for (const pkg of cartItemsByPackage) {
            try {
                const response = await fetch(`${API_BASE_URL}/shop/place_order.php`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        ...shipping,
                        fulfillment_source: shipping.fulfillmentSource,
                        franchise_id: shipping.franchiseId,
                        user_package_id: pkg.user_package_id
                    })
                });
                const data = await response.json();
                if (data.success) {
                    successCount++;
                } else {
                    failCount++;
                    showAlert(data.message || 'Failed to place order', 'error');
                }
            } catch (error) {
                failCount++;
                showAlert('Network error occurred', 'error');
            }
        }

        setPlacingOrder(false);

        if (successCount > 0 && failCount === 0) {
            showAlert(`All ${successCount} orders placed successfully!`, 'success');
            setShipping({
                name: '', address: '', city: '', state: '', zip: '', mobile: '',
                fulfillmentSource: 'admin', franchiseId: ''
            });
            fetchAllData();
            navigate('/orders');
        } else if (successCount > 0 && failCount > 0) {
            showAlert(`${successCount} orders placed, ${failCount} failed. Please check your orders.`, 'warning');
            fetchAllData();
        } else {
            showAlert('Failed to place orders. Please try again.', 'error');
        }
    };

    const getPackageTotal = (items) => {
        return items.reduce((acc, item) => acc + (parseFloat(item.price) * parseInt(item.quantity)), 0);
    };

    const getPackagePV = (items) => {
        return items.reduce((acc, item) => acc + (parseFloat(item.pv_points) * parseInt(item.quantity)), 0);
    };

    const grandTotal = cartItemsByPackage.reduce((acc, pkg) => acc + getPackageTotal(pkg.items), 0);
    const grandPV = cartItemsByPackage.reduce((acc, pkg) => acc + getPackagePV(pkg.items), 0);
    const totalItems = cartItemsByPackage.reduce((acc, pkg) => acc + pkg.items.reduce((a, i) => a + parseInt(i.quantity), 0), 0);
    const totalBonusItems = cartItemsByPackage.reduce((acc, pkg) =>
        acc + pkg.items.reduce((a, i) => {
            const buy = parseInt(i.buy_quantity || 0);
            const free = parseInt(i.get_free_quantity || 0);
            return a + (buy > 0 ? Math.floor(i.quantity / buy) * free : 0);
        }, 0), 0);
    const totalProducts = cartItemsByPackage.reduce((acc, pkg) => acc + pkg.items.length, 0);

    // Check if all packages can checkout
    const allPackagesCanCheckout = cartItemsByPackage.every(pkg => {
        const pkgTotal = getPackageTotal(pkg.items);
        return parseFloat(pkg.remaining_balance) >= pkgTotal;
    });

    const insufficientPackagesCount = cartItemsByPackage.filter(pkg => {
        const pkgTotal = getPackageTotal(pkg.items);
        return parseFloat(pkg.remaining_balance) < pkgTotal;
    }).length;

    const somePackagesHaveLowQuantity = cartItemsByPackage.some(pkg => {
        const itemsCount = pkg.items.reduce((a, i) => a + parseInt(i.quantity), 0);
        const minRequired = pkg.original_price === 3700 ? 1 : 4;
        return itemsCount < minRequired;
    });

    return (
        <Layout>
            <div className="max-w-7xl mx-auto p-4 sm:p-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-black text-gray-900 flex items-center gap-3">
                            <CreditCard className="text-blue-600" size={28} />
                            Checkout
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">Review all your items and complete purchase</p>
                    </div>
                    <button
                        onClick={() => navigate('/shop')}
                        className="flex items-center gap-2 text-gray-500 font-bold hover:text-blue-600 transition-colors uppercase tracking-widest text-xs"
                    >
                        <ArrowLeft size={16} /> Continue Shopping
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center p-24">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : totalProducts > 0 ? (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Left Column - All Items */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Step Indicator */}
                            <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
                                <div className="flex items-center justify-between">
                                    {[
                                        { num: 1, label: 'Review Cart', icon: ShoppingCart },
                                        { num: 2, label: 'Shipping', icon: Truck },
                                        { num: 3, label: 'Confirm', icon: CheckCircle }
                                    ].map((s, idx) => (
                                        <React.Fragment key={s.num}>
                                            <button
                                                onClick={() => setStep(s.num)}
                                                className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${step === s.num
                                                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                                                    : step > s.num
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-gray-100 text-gray-400'
                                                    }`}
                                            >
                                                {step > s.num ? (
                                                    <CheckCircle size={18} />
                                                ) : (
                                                    <s.icon size={18} />
                                                )}
                                                <span className="font-bold text-sm hidden sm:inline">{s.label}</span>
                                            </button>
                                            {idx < 2 && (
                                                <div className={`flex-1 h-1 mx-2 rounded ${step > s.num ? 'bg-green-400' : 'bg-gray-200'}`} />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>

                            {/* Step 1: Review Cart */}
                            {step === 1 && (
                                <div className="space-y-4">
                                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                        <ShoppingBag size={20} className="text-blue-600" />
                                        All Products ({totalProducts} items from {cartItemsByPackage.length} packages)
                                    </h2>

                                    {/* Unified Product List */}
                                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                        <div className="divide-y divide-gray-100">
                                            {cartItemsByPackage.map(pkg => {
                                                const pkgTotal = getPackageTotal(pkg.items);
                                                const balance = parseFloat(pkg.remaining_balance);
                                                const canCheckout = balance >= pkgTotal;

                                                return (
                                                    <div key={pkg.user_package_id}>
                                                        {/* Package Header - Mini */}
                                                        <div className={`px-4 py-3 flex items-center justify-between ${canCheckout ? 'bg-gradient-to-r from-blue-50 to-indigo-50' : 'bg-gradient-to-r from-red-50 to-orange-50'}`}>
                                                            <div className="flex items-center gap-3">
                                                                <div className={`p-2 rounded-lg ${canCheckout ? 'bg-blue-100 text-blue-600' : 'bg-red-100 text-red-600'}`}>
                                                                    <Package size={16} />
                                                                </div>
                                                                <div>
                                                                    <h3 className="font-bold text-gray-900 text-sm">{pkg.package_name}</h3>
                                                                    <p className="text-xs text-gray-500">{pkg.items.length} products</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-3">
                                                                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-bold ${canCheckout ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                                    <Wallet size={12} />
                                                                    ₹{balance.toLocaleString()}
                                                                </div>
                                                                {!canCheckout && (
                                                                    <div className="flex items-center gap-1 text-red-600 text-xs font-bold">
                                                                        <AlertTriangle size={12} />
                                                                        Need ₹{(pkgTotal - balance).toLocaleString()} more
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Products in this package */}
                                                        {pkg.items.map(item => {
                                                            const buy = parseInt(item.buy_quantity || 0);
                                                            const free = parseInt(item.get_free_quantity || 0);
                                                            const freeUnits = buy > 0 ? Math.floor(item.quantity / buy) * free : 0;
                                                            const isBogoActive = buy > 0 && free > 0;

                                                            return (
                                                                <React.Fragment key={item.id}>
                                                                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 hover:bg-gray-50 transition-colors">
                                                                        <div className="flex items-center gap-4 flex-1">
                                                                            <div className="h-16 w-16 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-200">
                                                                                {item.image_url ? (
                                                                                    <img src={item.image_url} alt={item.product_name} className="h-full w-full object-cover" />
                                                                                ) : (
                                                                                    <ShoppingBag className="text-gray-300" size={24} />
                                                                                )}
                                                                            </div>
                                                                            <div className="flex-1 min-w-0">
                                                                                <div className="flex items-center gap-2 mb-0.5">
                                                                                    <h4 className="font-bold text-gray-900 text-sm truncate">{item.product_name}</h4>
                                                                                    {isBogoActive && (
                                                                                        <span className="bg-amber-100 text-amber-700 text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider border border-amber-200">
                                                                                            Offer: BUY {buy} GET {free}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                <div className="flex items-center gap-2 mt-1">
                                                                                    <span className="text-xs text-gray-500">₹{parseFloat(item.price).toLocaleString()}</span>
                                                                                    <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">{item.pv_points} PV</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto border-t sm:border-t-0 pt-3 sm:pt-0">
                                                                            {/* Quantity Controls */}
                                                                            <div className="flex items-center gap-2">
                                                                                <button
                                                                                    onClick={() => updateQuantity(item.id, parseInt(item.quantity), -1)}
                                                                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-600 transition-all"
                                                                                >
                                                                                    <Minus size={12} />
                                                                                </button>
                                                                                <span className="w-8 text-center font-black text-gray-900 text-sm">{item.quantity}</span>
                                                                                <button
                                                                                    onClick={() => updateQuantity(item.id, parseInt(item.quantity), 1)}
                                                                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-600 transition-all"
                                                                                >
                                                                                    <Plus size={12} />
                                                                                </button>
                                                                            </div>
                                                                            <div className="text-right min-w-[100px]">
                                                                                <p className="font-black text-gray-900 text-sm sm:text-base">₹{(parseFloat(item.price) * parseInt(item.quantity)).toLocaleString()}</p>
                                                                            </div>
                                                                            <button
                                                                                onClick={() => removeItem(item.id)}
                                                                                className="text-gray-300 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 transition-all"
                                                                            >
                                                                                <Trash2 size={18} />
                                                                            </button>
                                                                        </div>
                                                                    </div>

                                                                    {/* Free Item Bonus Row if eligible */}
                                                                    {freeUnits > 0 && (
                                                                        <div className="mx-4 ml-12 sm:ml-24 mb-3 p-3 bg-indigo-50/50 rounded-2xl border border-indigo-100/50 flex items-center justify-between gap-4 animate-fadeIn border-dashed border-l-4">
                                                                            <div className="flex items-center gap-3">
                                                                                <div className="p-2 bg-indigo-100 rounded-xl text-indigo-600 shadow-sm">
                                                                                    <Gift size={14} />
                                                                                </div>
                                                                                <div>
                                                                                    <p className="text-xs font-bold text-indigo-900">
                                                                                        Bonus Gift: {freeUnits} × {item.free_product_name || item.product_name}
                                                                                    </p>
                                                                                    <p className="text-[10px] text-indigo-500 font-bold uppercase tracking-[0.1em] mt-0.5">Applied Automatically</p>
                                                                                </div>
                                                                            </div>
                                                                            <div className="text-right">
                                                                                <p className="text-[10px] font-black text-white bg-indigo-600 px-2 py-0.5 rounded-lg uppercase tracking-widest shadow-md shadow-indigo-100">FREE</p>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => setStep(2)}
                                        className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold text-sm transition-all hover:bg-blue-700 shadow-lg shadow-blue-200 flex items-center justify-center gap-2"
                                    >
                                        Continue to Shipping <ChevronRight size={18} />
                                    </button>
                                </div>
                            )}

                            {/* Step 2: Shipping */}
                            {step === 2 && (
                                <div className="space-y-4">
                                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                        <Truck size={20} className="text-blue-600" />
                                        Shipping Details
                                    </h2>

                                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                                                    <User size={12} className="inline mr-1" /> Full Name
                                                </label>
                                                <input
                                                    required
                                                    placeholder="Enter your full name"
                                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all"
                                                    value={shipping.name}
                                                    onChange={e => setShipping({ ...shipping, name: e.target.value })}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                                                    <Phone size={12} className="inline mr-1" /> Mobile Number
                                                </label>
                                                <input
                                                    required
                                                    placeholder="Enter mobile number"
                                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all"
                                                    value={shipping.mobile}
                                                    onChange={e => setShipping({ ...shipping, mobile: e.target.value })}
                                                />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                                                    <MapPin size={12} className="inline mr-1" /> Full Address
                                                </label>
                                                <input
                                                    required
                                                    placeholder="House No, Street, Locality"
                                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all"
                                                    value={shipping.address}
                                                    onChange={e => setShipping({ ...shipping, address: e.target.value })}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">PIN Code</label>
                                                <div className="relative">
                                                    <input
                                                        required
                                                        maxLength={6}
                                                        placeholder="6-digit PIN"
                                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all"
                                                        value={shipping.zip}
                                                        onChange={e => {
                                                            const val = e.target.value.replace(/\D/g, '');
                                                            setShipping({ ...shipping, zip: val });
                                                            if (val.length === 6) handlePincodeLookup(val);
                                                        }}
                                                    />
                                                    {searchingPincode && (
                                                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                            <div className="w-4 h-4 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin"></div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">State</label>
                                                <select
                                                    required
                                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all appearance-none"
                                                    value={shipping.state}
                                                    onChange={e => handleStateChange(e.target.value)}
                                                >
                                                    <option value="">Select State</option>
                                                    {states.map(s => (
                                                        <option key={s.isoCode} value={s.name}>{s.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">City</label>
                                                <select
                                                    required
                                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all appearance-none"
                                                    value={shipping.city}
                                                    onChange={e => setShipping({ ...shipping, city: e.target.value })}
                                                >
                                                    <option value="">Select City</option>
                                                    {cities.map(c => (
                                                        <option key={c.name} value={c.name}>{c.name}</option>
                                                    ))}
                                                    {!cities.length && shipping.city && <option value={shipping.city}>{shipping.city}</option>}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="mt-6 border-t border-gray-100 pt-6">
                                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">
                                                Order Fulfillment By
                                            </label>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <button
                                                    type="button"
                                                    onClick={() => setShipping({ ...shipping, fulfillmentSource: 'admin', franchiseId: '' })}
                                                    className={`p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${shipping.fulfillmentSource === 'admin'
                                                        ? 'border-blue-600 bg-blue-50'
                                                        : 'border-gray-100 bg-gray-50 hover:bg-white hover:border-gray-200'
                                                        }`}
                                                >
                                                    <div className={`p-2 rounded-lg ${shipping.fulfillmentSource === 'admin' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                                                        <User size={20} />
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-sm text-gray-900">Admin Direct</p>
                                                        <p className="text-xs text-gray-500">Order processed by central admin</p>
                                                    </div>
                                                    {shipping.fulfillmentSource === 'admin' && (
                                                        <CheckCircle className="ml-auto text-blue-600" size={20} />
                                                    )}
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={() => setShipping({ ...shipping, fulfillmentSource: 'franchise' })}
                                                    className={`p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${shipping.fulfillmentSource === 'franchise'
                                                        ? 'border-blue-600 bg-blue-50'
                                                        : 'border-gray-100 bg-gray-50 hover:bg-white hover:border-gray-200'
                                                        }`}
                                                >
                                                    <div className={`p-2 rounded-lg ${shipping.fulfillmentSource === 'franchise' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                                                        <Package size={20} />
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-sm text-gray-900">Franchise Shop</p>
                                                        <p className="text-xs text-gray-500">Collect from local franchise</p>
                                                    </div>
                                                    {shipping.fulfillmentSource === 'franchise' && (
                                                        <CheckCircle className="ml-auto text-blue-600" size={20} />
                                                    )}
                                                </button>
                                            </div>

                                            {shipping.fulfillmentSource === 'franchise' && (
                                                <div className="mt-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                                                        Select Franchise
                                                    </label>
                                                    <div className="relative">
                                                        <select
                                                            required
                                                            className="w-full px-4 py-3 bg-white border-2 border-blue-100 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none"
                                                            value={shipping.franchiseId}
                                                            onChange={e => setShipping({ ...shipping, franchiseId: e.target.value })}
                                                        >
                                                            <option value="">-- Choose a Franchise --</option>
                                                            {franchises.map(f => (
                                                                <option key={f.id} value={f.id}>
                                                                    {f.franchise_name} — {f.location} ({f.franchise_type.toUpperCase()})
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                                                            <ChevronRight className="rotate-90 text-gray-400" size={16} />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex gap-3 mt-6">
                                            <button
                                                onClick={() => setStep(1)}
                                                className="flex-1 py-3 bg-gray-100 text-gray-600 rounded-xl font-bold text-sm hover:bg-gray-200 transition-all flex items-center justify-center gap-2"
                                            >
                                                <ArrowLeft size={16} /> Back
                                            </button>
                                            <button
                                                onClick={() => setStep(3)}
                                                className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold text-sm transition-all hover:bg-blue-700 shadow-lg shadow-blue-200 flex items-center justify-center gap-2"
                                            >
                                                Review Order <ChevronRight size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Confirm */}
                            {step === 3 && (
                                <div className="space-y-4">
                                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                        <CheckCircle size={20} className="text-green-600" />
                                        Order Confirmation
                                    </h2>

                                    {/* Shipping Summary */}
                                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="font-bold text-gray-900 flex items-center gap-2">
                                                <Truck size={16} className="text-blue-600" /> Fulfillment & Shipping
                                            </h3>
                                            <button onClick={() => setStep(2)} className="text-blue-600 text-xs font-bold hover:underline">Edit</button>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="bg-gray-50 rounded-xl p-4">
                                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-2">Shipping Information</p>
                                                <p className="font-bold text-gray-900">{shipping.name}</p>
                                                <p className="text-sm text-gray-600 mt-1">{shipping.address}</p>
                                                <p className="text-sm text-gray-600">{shipping.city}, {shipping.state} - {shipping.zip}</p>
                                                <p className="text-sm text-gray-600 mt-1">📞 {shipping.mobile}</p>
                                            </div>
                                            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                                                <p className="text-[10px] text-blue-400 font-bold uppercase tracking-wider mb-2">Fulfillment Method</p>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white">
                                                        {shipping.fulfillmentSource === 'admin' ? <User size={20} /> : <Package size={20} />}
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-blue-900">
                                                            {shipping.fulfillmentSource === 'admin' ? 'Admin Direct' : 'Franchise Delivery'}
                                                        </p>
                                                        {shipping.fulfillmentSource === 'franchise' && (
                                                            <p className="text-xs text-blue-600 mt-0.5">
                                                                Store: {franchises.find(f => f.id.toString() === shipping.franchiseId.toString())?.franchise_name || 'Selected Franchise'}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Order Summary */}
                                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                                        <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                                            <ShoppingBag size={16} className="text-blue-600" /> Order Summary
                                        </h3>
                                        <div className="space-y-3">
                                            {cartItemsByPackage.map(pkg => (
                                                <div key={pkg.user_package_id} className="bg-gray-50 rounded-xl p-3">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="font-bold text-gray-900 text-sm flex items-center gap-2">
                                                            <Package size={14} className="text-blue-600" />
                                                            {pkg.package_name}
                                                        </span>
                                                        <span className="font-black text-gray-900">₹{getPackageTotal(pkg.items).toLocaleString()}</span>
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {pkg.items.map(item => (
                                                            <span key={item.id} className="inline-block bg-white px-2 py-0.5 rounded mr-1 mb-1">
                                                                {item.product_name} × {item.quantity}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {insufficientPackagesCount > 0 && (
                                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                                            <AlertTriangle className="text-red-500 flex-shrink-0" size={20} />
                                            <div>
                                                <p className="font-bold text-red-700">Cannot Complete Purchase</p>
                                                <p className="text-sm text-red-600 mt-1">
                                                    {insufficientPackagesCount} package(s) have insufficient shopping wallet balance.
                                                    Please go back and adjust your cart.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {somePackagesHaveLowQuantity && (
                                        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-start gap-3">
                                            <AlertTriangle className="text-orange-500 flex-shrink-0" size={20} />
                                            <div>
                                                <p className="font-bold text-orange-700">Minimum Quantity Required</p>
                                                <p className="text-sm text-orange-600 mt-1">
                                                    Each package order must meet the minimum item requirement (4 items normally, or 1 item for Starter 3700 package). Please increase quantities or add more products.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => setStep(2)}
                                            className="flex-1 py-3 bg-gray-100 text-gray-600 rounded-xl font-bold text-sm hover:bg-gray-200 transition-all flex items-center justify-center gap-2"
                                        >
                                            <ArrowLeft size={16} /> Back
                                        </button>
                                        <button
                                            onClick={handlePlaceAllOrders}
                                            disabled={placingOrder || !allPackagesCanCheckout || somePackagesHaveLowQuantity}
                                            className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${allPackagesCanCheckout && !placingOrder && !somePackagesHaveLowQuantity
                                                ? 'bg-green-600 text-white hover:bg-green-700 shadow-lg shadow-green-200'
                                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                }`}
                                        >
                                            {placingOrder ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                    Placing Orders...
                                                </>
                                            ) : (
                                                <>
                                                    <CheckCircle size={16} /> Place All Orders
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right Column - Order Summary (Sticky) */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sticky top-4">
                                <h3 className="text-lg font-black text-gray-900 mb-4">Order Summary</h3>

                                {/* Quick Stats */}
                                <div className="space-y-3 mb-4">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-gray-500">Products</span>
                                        <span className="font-bold text-gray-900">{totalProducts} items</span>
                                    </div>
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-gray-500">Total Units</span>
                                        <span className="font-bold text-gray-900">
                                            {totalItems} purchased {totalBonusItems > 0 && `+ ${totalBonusItems} FREE`}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-gray-500">Packages</span>
                                        <span className="font-bold text-gray-900">{cartItemsByPackage.length}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-gray-500">Total PV</span>
                                        <span className="font-bold text-blue-600">{grandPV.toLocaleString()} PV</span>
                                    </div>
                                    {totalBonusItems > 0 && (
                                        <div className="flex justify-between items-center text-sm p-2 bg-indigo-50 rounded-lg border border-indigo-100/50 mt-1">
                                            <span className="text-indigo-600 font-bold flex items-center gap-1.5">
                                                <Gift size={12} /> Free Gifts
                                            </span>
                                            <span className="font-black text-indigo-700">{totalBonusItems} items Included</span>
                                        </div>
                                    )}
                                </div>

                                <div className="border-t border-gray-100 pt-4 mb-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-500 font-medium">Subtotal</span>
                                        <span className="font-bold text-gray-900">₹{grandTotal.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between items-center mt-2">
                                        <span className="text-gray-500 font-medium">Shipping</span>
                                        <span className="font-bold text-green-600">FREE</span>
                                    </div>
                                </div>

                                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 rounded-xl text-white">
                                    <div className="flex justify-between items-center">
                                        <span className="font-bold">Grand Total</span>
                                        <span className="text-2xl font-black">₹{grandTotal.toLocaleString()}</span>
                                    </div>
                                    <p className="text-xs text-blue-100 mt-1">Using Shopping Wallet Balance</p>
                                </div>

                                {/* Package Balances */}
                                <div className="mt-4 space-y-2">
                                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Package Balances</p>
                                    {cartItemsByPackage.map(pkg => {
                                        const pkgTotal = getPackageTotal(pkg.items);
                                        const balance = parseFloat(pkg.remaining_balance);
                                        const canCheckout = balance >= pkgTotal;

                                        return (
                                            <div key={pkg.user_package_id} className={`flex items-center justify-between p-2 rounded-lg text-xs ${canCheckout ? 'bg-green-50' : 'bg-red-50'}`}>
                                                <span className={`font-bold ${canCheckout ? 'text-green-700' : 'text-red-700'}`}>
                                                    {pkg.package_name}
                                                </span>
                                                <div className="text-right">
                                                    <span className={`font-bold ${canCheckout ? 'text-green-700' : 'text-red-700'}`}>
                                                        ₹{balance.toLocaleString()}
                                                    </span>
                                                    <span className="text-gray-400 mx-1">/</span>
                                                    <span className="text-gray-600">₹{pkgTotal.toLocaleString()}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {!allPackagesCanCheckout && (
                                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                                        <p className="text-xs text-yellow-700 font-bold flex items-center gap-1">
                                            <AlertTriangle size={12} />
                                            Some packages have insufficient balance
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-24 bg-white rounded-3xl border border-gray-100">
                        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <ShoppingCart size={40} className="text-gray-200" />
                        </div>
                        <h3 className="text-xl font-black text-gray-900 uppercase tracking-tight">Your Cart is Empty</h3>
                        <p className="text-gray-400 font-bold text-xs uppercase tracking-widest mt-2">Add items from the shop to get started</p>
                        <button onClick={() => navigate('/shop')} className="mt-6 px-6 py-3 bg-gray-900 text-white rounded-xl font-bold shadow-lg hover:bg-black transition-all text-sm">
                            Browse Shop
                        </button>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default Checkout;
